import { ref, computed, watchPostEffect } from 'vue'
import { isBooksyCom } from '@booksy-com/vue-tools'
import { isNil, cond, T as stubTrue } from 'ramda'

import {
  CONNECTIONS_STATUSES,
  LOCAL_STORAGE_LAST_READER_SN,
  READERS,
  READER_ACTION_STATUSES,
  READER_STATUSES,
} from '@/components/settings/payments/payment-types/booksy-card-reader/consts.js'
import { getBcrConnectInfo } from '@/lib/analytics/helpers.js'

import { useCore } from '@/composables/core'
import { useToast } from '@/composables/toast'
import { useAnalytics } from '@/composables/analytics'
import { useStorage } from '@/composables/storage'
import { useDevLogger } from '@/composables/dev-logger'
import { useStripeSdi } from './sdi/index.js'
import { createStripeSdk } from './sdk/index.js'
import { createStripeWebview } from './webView/index.js'
import {
  getStripeConnectionToken,
  getStripeReaders,
} from '@/api/index.js'

import {
  getPaymentIntent,
  cancelPaymentIntent,
} from '@/composables/payments/payment-intent-functions/index.js'

const logger = useDevLogger('booksy-card-reader')

let sdk = null
let sdi = null
const webView = ref(null)

let watchConnectionStatus = null

const initialized = ref(false)
const notSupported = ref(false)
// last serial number of connected reader
const lastReaderSN = ref(null)
const connectingReaderSN = ref(null)
const activeReader = ref(null)

export async function createBooksyCardReader () {
  logger.debug('initializing')

  if (initialized.value) throw new Error('BCR was already created')

  const { store } = useCore()

  const { pos__stripe_terminal } = store.state.routingConfig.config

  if (!(pos__stripe_terminal && store.state.business.pos?.stripe_terminal_enabled)) {
    notSupported.value = true
    return false
  }

  // temporary move old connected SN to json strignify localstorage
  const storage = useStorage()
  const lastSN = localStorage.getItem(LOCAL_STORAGE_LAST_READER_SN)
  storage.setItem(storage.KEYS.BCR_LAST_CONNECTED_READER_SN, lastSN)

  await store.dispatch('business/loadStripeAccount')

  sdk = await createStripeSdk({ getConnectionToken, handleUnexpectedTerminalDisconnect })
  sdi = useStripeSdi()

  try {
    const isTablet = store.getters['app/isTablet']
    if (isTablet) {
      webView.value = await createStripeWebview({ getConnectionToken, handleUnexpectedTerminalDisconnect })
    }
  } catch (error) {
    logger.error(error)
  }

  watchConnectionStatus = watchPostEffect(() => {
    if (
      sdk.connectionStatus.value !== CONNECTIONS_STATUSES.CONNECTED
      || (webView.value && webView.value.bluetoothConnectionStatus !== CONNECTIONS_STATUSES.CONNECTED)
    ) {
      activeReader.value = null
    }
  })

  lastReaderSN.value = storage.getItem(storage.KEYS.BCR_LAST_CONNECTED_READER_SN)

  initialized.value = true
  logger.debug('initialized')

  try {
    await connectLastReader()
  } catch (error) {
    logger.error(error)
  }
}

export async function destroyBooksyCardReader () {
  if (!initialized.value) return

  logger.debug('destroying')

  await disconnectReader()

  initialized.value = false
  lastReaderSN.value = null
  watchConnectionStatus?.()

  sdk.destroyService()
  webView.value?.destroyService()

  sdk = null
  sdi = null
  webView.value = null
}

export function useBooksyCardReader () {
  if (notSupported.value) return
  if (!initialized.value) {
    throw new Error('useBooksyCardReader called before createBooksyCardReader')
  }

  const connectionStatus = computed(() => {
    const isWebViewCheckingStatus = () => webView.value && webView.value.bluetoothConnectionStatus !== CONNECTIONS_STATUSES.NOT_CONNECTED
    const isSdkCheckingStatus = () => sdk.connectionStatus.value !== CONNECTIONS_STATUSES.NOT_CONNECTED
    const isSDICheckingStatus = () => sdi.isSDIMode.value && activeReader.value

    const statusGetter = cond([
      [isWebViewCheckingStatus, () => webView.value.bluetoothConnectionStatus],
      [isSdkCheckingStatus, () => sdk.connectionStatus.value],
      [isSDICheckingStatus, () => CONNECTIONS_STATUSES.CONNECTED],
      [stubTrue, () => CONNECTIONS_STATUSES.NOT_CONNECTED],
    ])

    return statusGetter()
  })

  return {
    initialized,
    activeReader: computed(() => activeReader.value),
    connectingReaderSN: computed(() => connectingReaderSN.value),
    isSDIMode: sdi.isSDIMode,
    switchIntegrationModeToSDI: sdi.switchIntegrationModeToSDI,
    shouldSupportM2: computed(() => !isNil(webView.value)),
    updateStatusM2: webView.value?.updateStatus,
    isBluetoothReader: webView.value?.isBluetoothReader || (() => false),
    bluetoothReaders: computed(() => webView.value?.readers),
    bluetoothDiscoverReadersStart: webView.value?.bluetoothDiscoverReadersStart,
    bluetoothDiscoverReadersCancel: webView.value?.bluetoothDiscoverReadersCancel,
    simulatorConfig: sdk.simulatorConfig,
    setSimulatedTerminal: sdk.setSimulatedTerminal,
    collectPaymentMethod: sdk.collectPaymentMethod,
    connectionStatus,
    paymentStatus: sdk.paymentStatus,
    processPayment: sdk.processPayment,
    cancelCollectPaymentMethod: sdk.cancelCollectPaymentMethod,
    getPaymentIntent,
    getRegisteredReaders,
    cancelPaymentIntent,
    connectReader,
    connectLastReader,
    disconnectReader,
    // exported only for testing purposes
    handleUnexpectedTerminalDisconnect,
  }
}

async function getConnectionToken () {
  const { store } = useCore()
  const businessId = store.getters['business/businessId']

  const response = await getStripeConnectionToken(businessId)

  return response.data.connection_token
}

async function handleUnexpectedTerminalDisconnect () {
  try {
    if (sdk.wifiCheckoutPending.value) return

    logger.debug('Unexpected terminal disconnect. Trying to connect to the previous reader')
    await connectLastReader()
  } catch (error) {
    _resetReaderState()
    logger.error(error)
  }
}

async function connectLastReader () {
  logger.debug(`connectLastReader: ${lastReaderSN.value}`)

  if (!lastReaderSN.value) {
    logger.debug('lastReaderSN not set')
    return
  }

  const readers = await getRegisteredReaders()

  const reader = readers.find(reader => reader.serial_number === lastReaderSN.value)
  if (!reader) {
    throw Error('last reader not found in registeredReaders')
  }

  if (reader.isBluetooth) {
    if (!webView.value) throw Error('M2 not supported, native app update required!')

    try {
      await webView.value.checkPermissions()
    } catch (error) {
      logger.debug('Bluetooth or location permissions not granted. Please grant them in the app settings')
      throw error
    }
  }

  await connectReader(reader, undefined, true)
}

async function connectReader (reader, label) {
  logger.debug(`connecting: ${reader.serial_number}`)

  await disconnectReader()

  connectingReaderSN.value = reader.serial_number

  try {
    const readers = await getRegisteredReaders()
    if (!reader.isBluetooth && sdi.isSDIMode.value) {
      await sdi.checkIfReaderIsOnline(readers, reader)
    } else {
      const id = readers.find(r => r.serial_number === reader.serial_number)?.id
      await _connectWebViewOrSDKReader(reader, id, label)
    }

    activeReader.value = {
      ...reader,
      label: label || reader.label,
    }
    lastReaderSN.value = reader.serial_number

    const storage = useStorage()
    storage.setItem(storage.KEYS.BCR_LAST_CONNECTED_READER_SN, reader.serial_number)

    logger.debug(`set default reader: ${reader.serial_number}`)
  } finally {
    connectingReaderSN.value = null
  }
}

async function _connectWebViewOrSDKReader (reader, id, label) {
  const connectResult = reader.isBluetooth
    ? await webView.value.connect(reader)
    : await sdk.connect(reader, label)

  const connectionInfo = getBcrConnectInfo(reader)
  const error = connectResult?.error || connectResult?.error_description
  const analytics = useAnalytics()
  const { EVENTS } = analytics

  if (error) {
    analytics.dispatch(EVENTS.BCR_CARD_READER_CONNECT, {
      ...connectionInfo,
      stripe_bcr_failure_reason: error.message,
      stripe_bcr_failure_code: error.code,
    })
    throw error
  }
  analytics.dispatch(EVENTS.BCR_CARD_READER_CONNECT, connectionInfo)

  sdk.setSimulatedTerminal({ reader })

  if (webView.value && label) {
    await webView.value.setReaderLabel(reader.serial_number, label, id)
  }
}

async function getRegisteredReaders () {
  const { store } = useCore()
  const businessId = store.getters['business/businessId']
  const isTablet = store.getters['app/isTablet']

  const { data } = await getStripeReaders(businessId)

  const busyReadersSN = []
  const readers = data.readers
    .filter(reader => {
      const readers = Object.values(READERS)
      const READER = readers.find(READER => READER.device_type === reader.device_type)
      return READER && !READER.disabled && (isTablet || !READER.bluetooth)
    })
    .map(reader => {
      const isBluetooth = !!webView.value?.isBluetoothReader(reader.device_type)
      const isBusy = !isBluetooth && sdi.isSDIMode.value && reader.action?.status === READER_ACTION_STATUSES.IN_PROGRESS
      if (isBusy) {
        busyReadersSN.push(reader.serial_number)
        sdi.checkReaderBusyTime(reader)
      }
      return {
        ...reader,
        isBluetooth,
        status: isBusy ? READER_STATUSES.BUSY : reader.status,
      }
    })

  if (!busyReadersSN.length) {
    const storage = useStorage()
    storage.removeItem(storage.KEYS.BCR_BUSY_READERS)
  }

  if (busyReadersSN.includes(activeReader.value?.serial_number)) {
    await disconnectReader()
  }

  if (isTablet && !isBooksyCom()) {
    readers.push({ status: READER_STATUSES.OFFLINE, serial_number: 'STRM2SIMULATOR1', location: 'st_simulated', device_type: 'stripe_m2', isBluetooth: true })
  }

  if (sdk.simulatorConfig.simulated && !isBooksyCom()) {
    const simulatedData = await sdk.discoverReaders({
      simulated: sdk.simulatorConfig.simulated,
    })
    if (simulatedData?.discoveredReaders?.length) readers.push(simulatedData.discoveredReaders[0])
  }

  return readers
}

async function disconnectReader () {
  logger.debug(`disconnecting: ${activeReader.value?.serial_number}`)

  const { store } = useCore()
  const toast = useToast()
  const isTablet = store.getters['app/isTablet']

  if (isTablet && webView.value) {
    await webView.value.disconnect()
  }

  if (!sdi.isSDIMode.value) {
    try {
      await sdk.disconnect()
    } catch (error) {
      logger.error(error)
      toast.error('Failed to disconnect reader')
    }
  }

  _resetReaderState()
}

function _resetReaderState () {
  logger.debug('reseting reader state')
  connectingReaderSN.value = null
  activeReader.value = null
  webView.value?.resetUpdateState()
  sdk.wifiCheckoutPending.value = false
}
